import {
    useFormaliteService,
    useFormulaireService,
    useOffreService,
} from '@europrocurement/l2d-domain';
import { PrestationJsonldPrestationRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useOptionComplementaire } from '@europrocurement/l2d-modules/hooks';
import { castIriToId } from '@europrocurement/l2d-utils';

export type DossierPrestation = {
    label: string;
    dossierId: number;
    formaliteId: number;
    catalogueId: number;
    prestationId: number;
    prestationJson: object;
    complete: boolean;
    deleted: boolean;
};

export type DossierFormulaire = DossierPrestation & {
    formulaireId?: number;
    formulaireJson?: unknown;
};

const useDossierFormulaire = () => {
    const { formulairesApi } = useFormulaireService();
    const { formaliteApi, prestationFormaliteApi } = useFormaliteService();
    const { catalogsApi } = useOffreService();
    const { catalogueOptionsToFormulaire } = useOptionComplementaire();

    const prestationTransformer = (
        prestation: PrestationJsonldPrestationRead,
    ): DossierPrestation => ({
        label: prestation.libelle as string,
        dossierId: castIriToId(prestation?.dossier as string),
        formaliteId: castIriToId(prestation?.formalite as string),
        catalogueId: prestation?.catalogue?.id as number,
        prestationId: prestation.id as number,
        prestationJson: JSON.parse(prestation.json || '{}'),
        complete: false,
        deleted: prestation.deleted as boolean,
    });

    const getDossierPrestations = async (dossierId: number): Promise<Array<DossierPrestation>> => {
        const requestParameters = { dossier: dossierId as unknown as string };
        const result =
            await prestationFormaliteApi.apiDossiersDossierPrestationsGetCollection(
                requestParameters,
            );

        return result.data['hydra:member'].map(prestationTransformer);
    };

    const getFormaliteFormulaire = async (dossierId: number): Promise<Array<DossierFormulaire>> => {
        const prestations = await getDossierPrestations(dossierId);
        const promises = prestations.map(async (prestation) => {
            const formalite = await formaliteApi.apiFormalitesIdGet({
                id: prestation.formaliteId as unknown as string,
            });
            const formulaireId = formalite?.data?.formulaire
                ? castIriToId(formalite?.data?.formulaire)
                : undefined;

            if (!formulaireId) {
                return prestation;
            }

            const formulaire = await formulairesApi.apiFormulaireFormalitesIdGet({
                id: formulaireId as unknown as string,
            });
            const formulaireJson = formulaire?.data?.formulaire
                ? JSON.parse(formulaire.data.formulaire)
                : undefined;
            const catalog = await catalogsApi.apiCataloguesIdGet({
                id: prestation.catalogueId as unknown as string,
            });
            const options = await catalogueOptionsToFormulaire(catalog?.data?.options || []);

            if (options && formulaireJson) {
                formulaireJson.pages.push(options);
            }

            return {
                ...prestation,
                formulaireId,
                formulaireJson,
            };
        });

        const result = await Promise.all(promises);

        return result.flat();
    };

    const setDossierPrestation = async (
        formulaire: DossierFormulaire,
    ): Promise<DossierFormulaire> => {
        const result = await prestationFormaliteApi.apiPrestationsIdPut({
            id: formulaire.prestationId as unknown as string,
            prestationJsonldPrestationUpdate: {
                libelle: formulaire.label,
                json: JSON.stringify(formulaire.prestationJson),
            },
        });

        return {
            ...prestationTransformer(result.data),
            formulaireId: formulaire.formulaireId,
            formulaireJson: formulaire.formulaireJson,
        };
    };

    return { getFormaliteFormulaire, setDossierPrestation };
};

export default useDossierFormulaire;
